type LocationEntry = [filenamePart: string, name: string]
type LocationGroup = [regionName: string, locations: LocationEntry[]]

export interface ResortDropdownsSelectors {
  feature: string
  location: string
}

const PLACEHOLDER_VALUE = "-"

function placeholderOption(text: string) {
  const option = new Option(text, PLACEHOLDER_VALUE)
  option.disabled = true
  return option
}

function placeholderText(select: HTMLSelectElement) {
  return select.dataset.placeholder || "Choose resort"
}

function navigatesOnChange(select: HTMLSelectElement) {
  return select.dataset.navigateOnChange === "true"
}

function replaceOptions(select: HTMLSelectElement, options: HTMLOptionElement[]) {
  while (select.lastChild) select.removeChild(select.lastChild)
  options.forEach((option) => select.appendChild(option))
}

function appendLocationOptions(select: HTMLSelectElement, groups: LocationGroup[]) {
  groups.forEach(([regionName, locations]) => {
    const options = locations.map(([filenamePart, name]) => new Option(name, filenamePart))
    if (regionName && groups.length > 1) {
      const optgroup = document.createElement("optgroup")
      optgroup.label = regionName
      options.forEach((option) => optgroup.appendChild(option))
      select.appendChild(optgroup)
    } else {
      options.forEach((option) => select.appendChild(option))
    }
  })
}

function goToResort(filenamePart: string, forecastDays: string) {
  const target = window.top || window
  target.location.href = `/resorts/${filenamePart}/${forecastDays}/mid`
}

// Country/feature select that loads the matching resorts into the location
// select(s) of the same [data-resort-dropdowns] container. A location select
// with data-navigate-on-change="true" gets a disabled placeholder option
// (data-placeholder) and navigates to the chosen resort's forecast page
// (data-forecast-days sets the path segment); without it the first loaded
// resort is left selected and submitting is up to the page.
export class ResortDropdowns {
  static init(parent: ParentNode, selectors: ResortDropdownsSelectors): ResortDropdowns[] {
    return Array.from(parent.querySelectorAll<HTMLElement>("[data-resort-dropdowns]"))
      .map((root) => {
        const featureSelect = root.querySelector<HTMLSelectElement>(selectors.feature)
        const locationSelects = Array.from(
          root.querySelectorAll<HTMLSelectElement>(selectors.location),
        )
        return featureSelect && locationSelects.length > 0
          ? new ResortDropdowns(featureSelect, locationSelects)
          : null
      })
      .filter((instance): instance is ResortDropdowns => instance !== null)
  }

  constructor(
    private featureSelect: HTMLSelectElement,
    private locationSelects: HTMLSelectElement[],
  ) {
    this.addEventListeners()
  }

  private addEventListeners() {
    this.featureSelect.addEventListener("change", () => {
      const feature = this.featureSelect.value
      if (feature !== PLACEHOLDER_VALUE) this.loadResorts(feature)
    })

    this.locationSelects.forEach((select) => {
      if (!navigatesOnChange(select)) return

      const forecastDays = select.dataset.forecastDays || "6day"
      select.addEventListener("change", () => {
        const filenamePart = select.value
        if (filenamePart !== PLACEHOLDER_VALUE && filenamePart !== "") {
          goToResort(filenamePart, forecastDays)
        }
      })
    })
  }

  private async loadResorts(feature: string) {
    this.locationSelects.forEach((select) => {
      replaceOptions(select, [placeholderOption("Updating...")])
      select.disabled = true // eslint-disable-line no-param-reassign
    })

    try {
      const response = await fetch(`/resorts/list_by_feature/${encodeURIComponent(feature)}?v=2`)
      if (!response.ok) throw new Error(`Failed to load resorts: ${response.status}`)
      const groups = (await response.json()) as LocationGroup[]
      this.fillLocationSelects(groups)
    } catch {
      this.locationSelects.forEach((select) => {
        replaceOptions(select, [placeholderOption(placeholderText(select))])
        select.disabled = false // eslint-disable-line no-param-reassign
      })
    }
  }

  private fillLocationSelects(groups: LocationGroup[]) {
    this.locationSelects.forEach((select) => {
      select.disabled = false // eslint-disable-line no-param-reassign
      if (navigatesOnChange(select)) {
        const placeholder = placeholderOption(placeholderText(select))
        replaceOptions(select, [placeholder])
        appendLocationOptions(select, groups)
        placeholder.selected = true
      } else {
        replaceOptions(select, [])
        appendLocationOptions(select, groups)
      }
    })
    this.focusFirstLocationSelect()
  }

  private focusFirstLocationSelect() {
    const select = this.locationSelects[0]
    select.focus()
    // Android Chrome does not open the freshly filled dropdown on focus alone
    const webkitVersion = window.navigator.userAgent.match(/Android.*AppleWebKit\/([\d.]+)/)
    if (webkitVersion && parseFloat(webkitVersion[1]) >= 537) {
      select.dispatchEvent(
        new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window }),
      )
    }
  }
}
